/**
 * Gift Feature JavaScript - Production Ready
 * Handles gift modal functionality, variant selection, and cart integration
 */

class GiftFeature {
  constructor() {
    this.modal = null;
    this.selectedUpsells = new Set();
    this.selectedVariants = new Map();
    this.mainProduct = null;
    this.upsellProducts = {};
    this.shopifyMoney = window.Shopify?.currency?.active || 'EUR';
    this.debug = (window.GIFT_FEATURE_DEBUG === true) || (localStorage.getItem('GIFT_FEATURE_DEBUG') === '1');
    
    this.init();
  }

  init() {
    this.log('init:start');
    this.loadProductData();
    this.bindEvents();
    this.ensureCartGiftManager();
    this.log('init:complete');
  }

  ensureCartGiftManager() {
    this.log('ensureCartGiftManager:enter');
    // Initialize CartGiftManager if not already available
    if (!window.cartGiftManager && window.CartGiftManager) {
      try {
        window.cartGiftManager = new window.CartGiftManager();
      } catch (error) {
        this.log('ensureCartGiftManager:error:init', error);
      }
    } else if (!window.cartGiftManager) {
      // Try to wait and check again in case it loads later
      setTimeout(() => {
        if (!window.cartGiftManager && window.CartGiftManager) {
          try {
            window.cartGiftManager = new window.CartGiftManager();
          } catch (error) {
            this.log('ensureCartGiftManager:error:delayedInit', error);
          }
        }
      }, 1000);
    }
  }

  getGiftThreshold() {
    // Try multiple sources for gift threshold
    
    // 1. From existing CartGiftManager
    if (window.cartGiftManager?.threshold) {
      return window.cartGiftManager.threshold;
    }
    
    // 2. From window.theme
    if (window.theme?.giftThreshold) {
      return window.theme.giftThreshold;
    }
    
    // 3. From Shopify theme settings
    if (window.Shopify?.theme?.gift_threshold) {
      const threshold = parseInt(window.Shopify.theme.gift_threshold);
      return threshold;
    }
    
    // 4. From DOM elements with data attributes
    const progressElement = document.querySelector('[data-gift-threshold]');
    if (progressElement) {
      const threshold = parseInt(progressElement.dataset.giftThreshold);
      return threshold;
    }
    
    // 5. From gift progress container content
    const thresholdElement = document.querySelector('.gift-threshold');
    if (thresholdElement) {
      // Display string in the active currency format -> cents (symbol position / separators agnostic)
      const threshold = window.themeMoney.parse(thresholdElement.textContent);
      if (threshold > 0) {
        return threshold;
      }
    }
    
    // 6. Default fallback
    const defaultThreshold = 4900; // 4900 cents (fallback only; the real value comes from the gift_threshold setting)
    return defaultThreshold;
  }

  loadProductData() {
    this.log('loadProductData:enter');
    // Load main product data
    const productJsonEl = document.querySelector('.product-json');
    if (productJsonEl) {
      try {
        this.mainProduct = JSON.parse(productJsonEl.textContent);
      } catch (e) {
        this.log('loadProductData:error:productJson', e);
      }
    }

    // Load upsell products data
    const upsellJsonEl = document.querySelector('.gift-upsell-products-json');
    
    if (upsellJsonEl) {
      try {
        this.upsellProducts = JSON.parse(upsellJsonEl.textContent);
        
        // Make it available globally for compatibility
        window.giftProductData = this.upsellProducts;
      } catch (e) {
        this.log('loadProductData:error:upsellJson', e);
      }
    }
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      // Modal trigger
      if (e.target.matches('[data-gift-modal-trigger]')) {
        e.preventDefault();
        this.openModal();
        return;
      }

      // Modal close
      if (e.target.matches('[data-gift-modal-close]') || 
          e.target.closest('[data-gift-modal-close]') || 
          e.target.matches('.gift-modal__overlay')) {
        e.preventDefault();
        this.closeModal();
        return;
      }

      // Upsell toggle
      if (e.target.closest('.gift-upsell-toggle')) {
        e.preventDefault();
        const button = e.target.closest('.gift-upsell-toggle');
        this.toggleUpsell(button);
        return;
      }

      // Add to cart
      if (e.target.matches('.gift-modal__add-to-cart') || e.target.closest('.gift-modal__add-to-cart')) {
        e.preventDefault();
        this.addToCart();
        return;
      }
      
      // Variant option selection
      if (e.target.matches('.gift-variant-option')) {
        this.handleVariantSelection(e.target);
        return;
      }

      // Preset message buttons
      if (e.target.matches('.gift-prewritten-btn')) {
        e.preventDefault();
        this.setPresetMessage(e.target);
        return;
      }
    });

    // Message input counter
    document.addEventListener('input', (e) => {
      if (e.target.matches('#gift-message')) {
        this.updateMessageCounter(e.target);
      }
    });

    // ESC key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal) {
        this.closeModal();
      }
    });
  }

  openModal() {
    this.log('modal:open');
    this.modal = document.querySelector('#gift-modal');
    if (!this.modal) {
      return;
    }

      this.modal.style.display = 'block';
      document.body.style.overflow = 'hidden';
      
      this.initializeVariantSelections();
    this.updateTotalPrice();
      
    // Initialize message counter
      const messageInput = this.modal.querySelector('#gift-message');
      if (messageInput) {
        this.updateMessageCounter(messageInput);
    }
  }

  closeModal() {
    this.log('modal:close');
    if (!this.modal) return;

      this.modal.style.display = 'none';
      document.body.style.overflow = '';
      this.resetModal();
  }

  resetModal() {
    // Reset selections
    this.selectedUpsells.clear();
    this.selectedVariants.clear();
    
    // Reset UI
    document.querySelectorAll('.gift-upsell-item.selected').forEach(item => {
      item.classList.remove('selected');
    });
    
    // Reset gift message
    const messageInput = document.querySelector('#gift-message');
    if (messageInput) {
      messageInput.value = '';
      this.updateMessageCounter(messageInput);
    }

    // Reset prices
    this.updateTotalPrice();
  }

  getMainProductVariantId() {
    // Try multiple sources for the main product variant ID
    
    // 1. Try from main product data
    if (this.mainProduct && this.mainProduct.variants && this.mainProduct.variants.length > 0) {
      const selectedVariant = this.mainProduct.variants.find(v => v.selected) || 
                             this.mainProduct.variants.find(v => v.available) ||
                             this.mainProduct.variants[0];
      return selectedVariant.id;
    }
    
    // 2. Try from product form on page
    const productForm = document.querySelector('#add-to-cart-form, .product-form, form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('select[name="id"], input[name="id"], [name="id"]');
      if (variantInput && variantInput.value) {
        return parseInt(variantInput.value);
      }
    }
    
    // 3. Try from global product object
    if (window.product && window.product.variants && window.product.variants.length > 0) {
      const variant = window.product.variants.find(v => v.available) || window.product.variants[0];
      return variant.id;
    }
    
    // 4. Try from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      return parseInt(variantParam);
    }
    
    // 5. Fallback - try to find any variant input on the page
    const anyVariantInput = document.querySelector('[data-variant-id], .variant-id, #product-select');
    if (anyVariantInput) {
      const variantId = anyVariantInput.dataset.variantId || anyVariantInput.value;
      if (variantId) {
        return parseInt(variantId);
      }
    }
    
    // 6. Last resort fallback
    return 54963641942402; // Your fallback ID from the logs
  }

  initializeVariantSelections() {
    // Check what upsell items exist in the DOM
    const domUpsellItems = document.querySelectorAll('.gift-upsell-item');
    
    // Initialize default variant selections for each upsell product
    Object.keys(this.upsellProducts).forEach(productId => {
      // Skip debug info
      if (productId === 'debug') return;
      
      const product = this.upsellProducts[productId];
      
      // Check if variants array is empty but product exists
      if (product && (!product.variants || product.variants.length === 0)) {
        // Get variant data from the button (which comes from Liquid)
        const productElement = document.querySelector(`[data-product-id="${productId}"]`);
        const button = productElement?.querySelector('.gift-upsell-toggle');
        const variantId = button?.dataset?.variantId;
        const basePrice = button?.dataset?.basePrice;
        
        if (variantId && basePrice) {
          // Create the missing variant
          const realVariant = {
            id: parseInt(variantId),
            price: this.parsePrice(basePrice),
            available: true,
            title: 'Default',
            options: []
          };
          
          // Update the product with the real variant
          product.variants = [realVariant];
          this.selectedVariants.set(productId, parseInt(variantId));
        }
      } else if (product && product.variants && product.variants.length > 0) {
        // Find first available variant
        const firstAvailableVariant = product.variants.find(v => v.available);
        
        if (firstAvailableVariant) {
          this.selectedVariants.set(productId, firstAvailableVariant.id);
          
          // Update variant selection UI to match
          this.updateVariantSelectionUI(productId, firstAvailableVariant);
        }
      }
    });
    
    // If no JSON data but DOM elements exist, try to initialize from DOM
    if (Object.keys(this.upsellProducts).filter(k => k !== 'debug').length === 0 && domUpsellItems.length > 0) {
      // Note: DOM initialization is async but we'll let it run in background
      this.initializeFromDOM(domUpsellItems).catch(error => {
        // Silently handle errors
      });
    }
  }

  async fetchProductVariants(productId) {
    try {
      // Try different Shopify API endpoints
      const endpoints = [
        `/products/${productId}.js`,           // Direct product ID (might work)
        `/admin/api/2023-10/products/${productId}.json`, // Admin API
        `/api/2023-10/graphql`,               // GraphQL (would need different setup)
      ];
      
      // First try the simple products endpoint
      let response = await fetch(`/products/${productId}.js`);
      
      // If that fails, try the recommendations API which sometimes has product data
      if (!response.ok) {
        response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=1`);
        
        if (response.ok) {
          const data = await response.json();
          return null; // For now, we'll handle this case differently
        }
      }
      
      if (response.ok) {
        const productData = await response.json();
        
        // Update our upsell products with real data
        this.upsellProducts[productId] = productData;
        
        // Set default variant (first available)
        const firstAvailableVariant = productData.variants.find(v => v.available);
        if (firstAvailableVariant) {
          this.selectedVariants.set(productId, firstAvailableVariant.id);
        }
        
        return productData;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  async initializeFromDOM(domUpsellItems) {
    for (const [index, item] of domUpsellItems.entries()) {
      const productId = item.dataset.productId;
      
      // Get the toggle button
      const button = item.querySelector('.gift-upsell-toggle');
      const priceData = button?.dataset?.basePrice;
      const variantId = button?.dataset?.variantId; // Get the real variant ID from button
      
      if (variantId && priceData) {
        // Create product data structure with real variant ID
        const domBasedProduct = {
          id: productId,
          variants: [{
            id: parseInt(variantId), // Use the real variant ID from Liquid
            price: this.parsePrice(priceData),
            available: true,
            title: 'Default',
            options: []
          }]
        };
        
        this.upsellProducts[productId] = domBasedProduct;
        this.selectedVariants.set(productId, parseInt(variantId));
        
      } else if (priceData) {
        // Fallback: try to fetch from API
        const productData = await this.fetchProductVariants(productId);
        if (productData && productData.variants.length > 0) {
          // Product data was fetched successfully, continue to next item
          continue;
        }
      }
    }
    
    // Update global reference
    window.giftProductData = this.upsellProducts;
  }

  parsePrice(priceString) {
    // data-base-price carries integer cents ("2995"); a display string in the active
    // currency format is still understood. Returns cents.
    const cents = window.themeMoney.toCents(priceString);
    return Number.isNaN(cents) ? 0 : cents;
  }

  updateVariantSelectionUI(productId, variant) {
    const upsellItem = document.querySelector(`.gift-upsell-item[data-product-id="${productId}"]`);
    if (!upsellItem) {
      return;
    }

    // Update image
    const image = upsellItem.querySelector('.gift-upsell-image img');
    if (image && variant.featured_image) {
      image.src = variant.featured_image;
      image.alt = variant.title;
    }

    // Update price in the localized "+ add" label without altering translated text
    const addEl = upsellItem.querySelector('.gift-upsell-add');
    if (addEl) {
      const newPrice = this.formatPrice(variant.price);
      // The price lives in its own element, so no display string has to be parsed
      const addPriceEl = addEl.querySelector('.gift-upsell-add-price');
      if (addPriceEl) {
        addPriceEl.textContent = newPrice;
      } else {
        // Markup without the price element: keep the localized label, append the price
        const current = (addEl.textContent || '').trim();
        addEl.textContent = (current ? current + ' ' : '') + newPrice;
      }
    }

    // Set radio buttons to match variant
    if (variant.options) {
      variant.options.forEach((optionValue, index) => {
        if (optionValue) {
          const radio = upsellItem.querySelector(`input[data-option-index="${index}"][value="${optionValue}"]`);
          if (radio) {
            radio.checked = true;
          }
        }
      });
    }
  }

  handleVariantSelection(input) {
    const productId = input.dataset.productId;
    const variantId = input.dataset.variantId; // Get variant ID directly from Liquid!
    const variantPrice = input.dataset.variantPrice; // Get price directly from Liquid!
    const optionValue = input.value;

    if (!productId || !variantId) {
      return;
    }

    // SIMPLIFIED: Use the variant ID directly from Liquid data attribute
    this.selectedVariants.set(productId, parseInt(variantId));
    
    // Update UI to reflect new variant with price from Liquid
    const variantData = { 
      id: parseInt(variantId), 
      price: parseInt(variantPrice) || 0,
      priceFormatted: this.formatPrice(parseInt(variantPrice) || 0)
    };
    this.updateVariantSelectionUI(productId, variantData);
    
    // Update total price if this product is selected
    if (this.selectedUpsells.has(productId)) {
      this.updateTotalPrice();
    }
  }

  // SIMPLIFIED: No longer needed - using direct Liquid variant IDs
  getSelectedOptions(productId) {
    // This method is no longer used since we get variant IDs directly from Liquid
    return [];
  }

  // SIMPLIFIED: No longer needed - using direct Liquid variant IDs  
  findMatchingVariant(productId, selectedOptions) {
    // This method is no longer used since we get variant IDs directly from Liquid
    return null;
  }

  toggleUpsell(button) {
    const upsellItem = button.closest('.gift-upsell-item');
    if (!upsellItem) {
      return;
    }

    // Check if item is sold out
    if (upsellItem.classList.contains('gift-upsell-item--sold-out') || button.disabled) {
      return;
    }

    const productId = upsellItem.dataset.productId;
    const isSelected = upsellItem.classList.contains('selected');

    if (isSelected) {
      this.selectedUpsells.delete(productId);
      upsellItem.classList.remove('selected');
    } else {
      this.selectedUpsells.add(productId);
      upsellItem.classList.add('selected');
    }
    
    this.updateTotalPrice();
  }

  updateUpsellUI(productId, isSelected) {
    const upsellItem = this.modal.querySelector(`[data-product-id="${productId}"]`);
    if (!upsellItem) {
      return;
    }
    
    const toggleButton = upsellItem.querySelector('.gift-upsell-toggle');
    if (!toggleButton) {
      return;
    }
    
    // Update button style and state without changing localized inner text
    if (isSelected) {
      toggleButton.style.background = '#30bec4';
      toggleButton.style.color = '#fff';
      toggleButton.style.borderColor = '#30bec4';
      upsellItem.classList.add('gift-upsell-selected');
    } else {
      toggleButton.style.background = '#fff';
      toggleButton.style.color = '#20568a';
      toggleButton.style.borderColor = '#20568a';
      upsellItem.classList.remove('gift-upsell-selected');
    }
  }

  updateMessageCounter(input) {
    const counter = document.querySelector('.gift-message-count');
    if (counter) {
      counter.textContent = input.value.length;
    }
  }

  setPresetMessage(button) {
    const message = button.dataset.message;
    const messageInput = document.querySelector('#gift-message');
    
    if (messageInput && message) {
      messageInput.value = message;
      this.updateMessageCounter(messageInput);
      messageInput.focus();
    }
  }

  getCurrentMainVariant() {
    // Try to get from product form
    const productForm = document.querySelector('#add-to-cart-form');
    if (productForm) {
      const variantInput = productForm.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        const variantId = parseInt(variantInput.value);
        return variantId;
      }
    }

    // Try to get from selected variant in swatches
    const selectedRadio = document.querySelector('input[name="id"]:checked');
    if (selectedRadio) {
      const variantId = parseInt(selectedRadio.value);
      return variantId;
    }

    // Fallback to first available variant
    if (this.mainProduct && this.mainProduct.variants) {
      const firstAvailable = this.mainProduct.variants.find(v => v.available);
      const variantId = firstAvailable ? firstAvailable.id : null;
      return variantId;
    }

    return null;
  }
  
  calculateTotalPrice() {
    // Get main product price
    const mainVariantId = this.getMainProductVariantId();
    
    let mainProductPrice = 0;
    if (this.mainProduct && this.mainProduct.variants) {
      const mainVariant = this.mainProduct.variants.find(v => v.id === mainVariantId);
      if (mainVariant) {
        mainProductPrice = mainVariant.price;
      }
    }
    
    // Calculate upsells total using DOM data instead of JSON lookups
    let upsellsTotal = 0;
    const selectedUpsellsCount = this.selectedUpsells.size;
    
    this.selectedUpsells.forEach(productId => {
      const variantId = this.selectedVariants.get(productId);
      
      // FIXED: Get price from the selected variant input in DOM
      const selectedInput = this.modal.querySelector(`input[data-product-id="${productId}"][data-variant-id="${variantId}"]:checked`);
      if (selectedInput && selectedInput.dataset.variantPrice) {
        const price = parseInt(selectedInput.dataset.variantPrice);
        upsellsTotal += price;
      } else {
        // Fallback: try to get price from button data
        const button = this.modal.querySelector(`[data-product-id="${productId}"] .gift-upsell-toggle`);
        if (button && button.dataset.basePrice) {
          const fallbackPrice = this.parsePrice(button.dataset.basePrice);
          upsellsTotal += fallbackPrice;
        }
      }
    });
    
    const totalPrice = mainProductPrice + upsellsTotal;
    
    return totalPrice;
  }
  
  updateTotalPrice() {
    const totalPrice = this.calculateTotalPrice();
    const ctaButton = document.querySelector('.gift-modal__add-to-cart');
    const ctaText = ctaButton?.querySelector('.gift-cta-text');
    const ctaPrice = ctaButton?.querySelector('.gift-cta-price');
    
    if (ctaText && ctaPrice) {
    if (totalPrice > 0) {
        const priceText = ` - ${this.formatPrice(totalPrice)}`;
        ctaPrice.textContent = priceText;
        ctaPrice.style.display = 'inline';
    } else {
        ctaPrice.textContent = '';
        ctaPrice.style.display = 'none';
      }
    }
  }

  formatPrice(cents) {
    // Active currency format (symbol position, separators, decimals) from the shared formatter
    return window.themeMoney.format(cents);
  }

  // Utility: escape string for RegExp construction
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  async addToCart() {
    try {
      const t0 = performance.now();
      // Validate variants before proceeding
      const validationResult = this.validateVariants();
      if (!validationResult.valid) {
        this.log('addToCart:validation:error', validationResult.errors);
        alert(`Error: ${validationResult.errors.join(', ')}`);
        return;
      }
      
      const items = this.prepareCartItems();
      this.log('addToCart:preparedItems', items);
      
      if (items.length === 0) {
        this.log('addToCart:noItems');
        return;
      }
      // Get cart baseline for reliable sync
      const cartBefore = await this.fetchCart().catch((e) => { this.log('addToCart:fetchCart:before:error', e); return null; });
      const expectedCount = cartBefore ? cartBefore.item_count + items.length : null;
      this.log('addToCart:cartBefore', { item_count: cartBefore?.item_count, expectedCount });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        const addResp = await response.json().catch((e) => { this.log('addToCart:parseAddResponse:error', e); return {}; });
        this.log('addToCart:addResponse', addResp);

        // Close modal immediately for responsiveness
        this.closeModal();
        
        // Wait until cart reflects the new items, with retries
        const freshCart = await this.waitForCartCount(expectedCount, 8, 400).catch((e) => { this.log('addToCart:waitForCartCount:error', e); return null; });
        this.log('addToCart:freshCart', { ok: !!freshCart, item_count: freshCart?.item_count, total_price: freshCart?.total_price });
        
        // Update UI using freshest known cart (or fetch if needed)
        if (freshCart) {
          await this.updateCartDisplay(freshCart);
        } else {
          await this.updateCartDisplay();
        }
        const t1 = performance.now();
        this.log('addToCart:done', { duration_ms: Math.round(t1 - t0) });
        
        // Show success message after a brief delay to ensure cart is fully updated
        setTimeout(() => {
          this.showSuccessMessage();
        }, 500);
      } else {
        const errorData = await response.json();
        this.log('addToCart:addResponse:httpError', { status: response.status, errorData });
        
        // Show user-friendly error
        const errorMessage = errorData.description || errorData.message || 'Failed to add items to cart';
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      this.log('addToCart:exception', error);
      alert('An error occurred while adding items to cart. Please try again.');
    }
  }

  showSuccessMessage() {
    // Try to trigger existing cart drawer if available
    const cartDrawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (cartDrawer) {
      cartDrawer.click();
    }
    
    // Or show a simple success message
    const successMsg = document.createElement('div');
    successMsg.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4caf50;
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        z-index: 10000;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      ">
        ✅ Items added to cart!
      </div>
    `;
    
    document.body.appendChild(successMsg);
    
    setTimeout(() => {
      if (successMsg.parentNode) {
        successMsg.parentNode.removeChild(successMsg);
      }
    }, 3000);
  }

  async updateCartDisplay(providedCartData) {
    try {
      this.log('updateCartDisplay:enter', { provided: !!providedCartData });
      // FIXED: Use the exact same pattern as main product add to cart
      
      // 1. Use provided cart data or fetch fresh
      let cartData = providedCartData;
      if (!cartData) {
        const response = await fetch(`/cart.js?v=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          cache: 'no-store',
          credentials: 'same-origin'
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch cart: ${response.status}`);
        }
        cartData = await response.json();
      }
      this.log('updateCartDisplay:cartData', { item_count: cartData.item_count, total_price: cartData.total_price });
      
      // 2. Update cart drawer using theme's method (same as animal-cross-sell.js and component-cross-sells.js)
      if (window.wetheme?.cartDrawer?.updateCartDrawer) {
        window.wetheme.cartDrawer.updateCartDrawer(cartData);
      } else if (window.theme?.updateCartDrawer) {
        window.theme.updateCartDrawer(cartData);
      }
      
      // 3. Update cart count (same as theme.js updatedCart method) - with multiple selectors
      const cartCountSelectors = [
        '.cart-count', 
        '.cart__count', 
        '[data-cart-count]',
        '.cart-item-count',
        '.header-cart-count',
        '.cart-icon-count',
        '.js-cart-count',
        '.cart-count-header',
        '.cart-item-count-header--quantity'
      ];
      
      cartCountSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.textContent = cartData.item_count;
        });
      });
      
            // 4. Trigger cart drawer opening (enhanced with multiple methods)
      let cartOpened = false;
      
      // Method 1: Try theme-specific cart drawer triggers
      const cartDrawerTrigger = document.querySelector('[data-drawer-trigger="cart-drawer"], .js-cart-drawer, [data-drawer="drawer-cart"], #button-trigger-cart');
      if (cartDrawerTrigger) {
        cartDrawerTrigger.click();
        cartOpened = true;
      }
      
      // Method 2: Try wetheme cart drawer
      if (!cartOpened && window.wetheme?.cartDrawer?.openCartDrawer) {
        window.wetheme.cartDrawer.openCartDrawer();
        cartOpened = true;
      }
      
      // Method 3: Try jQuery trigger
      if (!cartOpened && window.jQuery) {
        window.jQuery('.cart-slider-wrapper, #cartSlideoutWrapper').trigger('cart:open');
        cartOpened = true;
      }
      
      // Method 4: Try direct DOM manipulation
      if (!cartOpened) {
        const cartSlideout = document.querySelector('#cartSlideoutWrapper, .cart-drawer, .drawer--cart');
        if (cartSlideout) {
          cartSlideout.classList.add('slideout-panel-visible');
          cartSlideout.classList.remove('slideout-panel-hidden');
          cartOpened = true;
        }
      }
      
      // 5. Dispatch cart update events (same as other components) - mark as from gift modal
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { ...cartData, fromGiftModal: true } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { ...cartData, fromGiftModal: true } }));
      document.dispatchEvent(new CustomEvent('product:added', { detail: { ...cartData, fromGiftModal: true } }));
      
      // 6. FIXED: Force gift progress update with multiple attempts
      await this.updateGiftProgress(cartData);
      
             // 7. ONLY trigger CartGiftManager if gift threshold just reached
       setTimeout(async () => {
         const freshCart = await fetch('/cart.js').then(res => res.json());
         await this.updateGiftProgress(freshCart);
         
         // Only trigger CartGiftManager if threshold was just reached
         const threshold = this.getGiftThreshold();
         if (freshCart.total_price >= threshold) {
           this.conditionallyTriggerCartGiftManager(freshCart);
         }
       }, 1000); // Increased delay to ensure cart state is stable
      
    } catch (error) {
      this.log('updateCartDisplay:error', error);
      // Fallback: page reload if cart update fails
      window.location.reload();
    }
  }

  async fetchCart() {
    this.log('fetchCart:enter');
    const res = await fetch(`/cart.js?v=${Date.now()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      cache: 'no-store',
      credentials: 'same-origin'
    });
    if (!res.ok) throw new Error('cart fetch failed');
    const data = await res.json();
    this.log('fetchCart:success', { item_count: data.item_count, total_price: data.total_price });
    return data;
  }

  async waitForCartCount(expectedCount, maxTries = 12, delayMs = 500) {
    // If we don't know the expected count, just fetch once
    if (expectedCount == null) {
      this.log('waitForCartCount:skip:unknownExpected');
      return await this.fetchCart().catch((e) => { this.log('waitForCartCount:skip:error', e); return null; });
    }
    this.log('waitForCartCount:start', { expectedCount, maxTries, delayMs });
    for (let i = 0; i < maxTries; i++) {
      const cart = await this.fetchCart().catch((e) => { this.log('waitForCartCount:fetch:error', e); return null; });
      if (cart && cart.item_count >= expectedCount) {
        this.log('waitForCartCount:met', { attempt: i + 1, item_count: cart.item_count });
        return cart;
      }
      this.log('waitForCartCount:notMet', { attempt: i + 1, item_count: cart?.item_count });
      // Exponential backoff after a few attempts
      const sleep = i >= 4 ? delayMs + i * 150 : delayMs;
      await new Promise(r => setTimeout(r, sleep));
    }
    this.log('waitForCartCount:timeout');
    return null;
  }

  log(event, payload) {
    if (!this.debug) return;
    try {
      console.debug('[GiftFeature]', event, payload || '');
    } catch (_) {}
  }

  async updateGiftProgress(cartData) {
    try {
      // Get gift settings from multiple sources
      const giftThreshold = this.getGiftThreshold();
      
      const currentTotal = cartData.total_price;
      const progress = Math.min((currentTotal / giftThreshold) * 100, 100);
      const remaining = giftThreshold - currentTotal;
      const isThresholdMet = currentTotal >= giftThreshold;
      
      // Update gift progress bar elements
      this.updateGiftProgressElements(progress, remaining, isThresholdMet, giftThreshold);
      
      // Trigger existing CartGiftManager if available
      if (window.cartGiftManager) {
        // Force CartGiftManager to update
        setTimeout(() => {
          window.cartGiftManager.debounceUpdate(100);
        }, 500);
      }
      
      // Also try to trigger any cart gift scripts manually
      this.triggerGiftLogic(cartData, giftThreshold);
      
    } catch (error) {
      // Silently handle errors
    }
  }

  updateGiftProgressElements(progress, remaining, isThresholdMet, threshold) {
    // Update progress bars
    document.querySelectorAll('.gift-progress').forEach(el => {
      el.style.width = `${progress}%`;
    });
    
    // Update remaining amount text
    document.querySelectorAll('.gift-progress-remaining').forEach(el => {
      const amountElement = el.querySelector('[data-remaining-amount]');
      if (amountElement) {
        // Dedicated amount element: only its text changes
        amountElement.textContent = this.formatPrice(remaining);
      } else {
        // Re-render from the translated template (data-template holds "__AMOUNT__")
        const remainingFormatted = this.formatPrice(remaining);
        const template = el.dataset.template;
        if (template && template.indexOf('__AMOUNT__') !== -1) {
          el.innerHTML = template.replace('__AMOUNT__', () => remainingFormatted);
        } else {
          el.innerHTML = el.innerHTML.replace(/\d[\d\s.,'\u2019\u00a0\u202f]*\d|\d/, () => remainingFormatted);
        }
      }
      el.style.display = isThresholdMet ? 'none' : 'block';
    });
    
    // Update achieved text
    document.querySelectorAll('.gift-progress-achieved').forEach(el => {
      el.style.display = isThresholdMet ? 'block' : 'none';
    });
    
    // Update gift label states
    document.querySelectorAll('.gift-label').forEach(el => {
      el.classList.toggle('is-active', isThresholdMet);
    });
    
    // Update threshold marker
    document.querySelectorAll('.gift-threshold').forEach(el => {
      el.classList.toggle('is-completed', isThresholdMet);
    });
  }

     triggerGiftLogic(cartData, threshold) {
     // Simplified gift logic - just log the status
     const shouldHaveGift = cartData.total_price >= threshold;
     
     if (shouldHaveGift) {
       // Gift threshold reached! Cart total reaches threshold
       // Conditional CartGiftManager trigger will handle automatic gift addition
     } else {
       const remaining = threshold - cartData.total_price;
       // Gift threshold not yet reached
     }
   }

   hideGiftProgress() {
     // Find and hide gift progress elements with smooth transition
     const progressContainers = document.querySelectorAll(
       '.gift-progress-container, ' +
       '.cart-gift-progress, ' +
       '[class*="gift-progress"], ' +
       '[class*="gift-bar"]'
     );
     
     progressContainers.forEach(container => {
       if (container && !container.classList.contains('gift-progress-hidden')) {
         container.classList.add('gift-progress-transitioning');
         container.style.opacity = '0';
         container.style.transform = 'translateY(-10px)';
         
         setTimeout(() => {
           container.style.display = 'none';
           container.classList.add('gift-progress-hidden');
         }, 300);
       }
     });
   }

   showGiftProgress() {
     // Find and show previously hidden gift progress elements with smooth transition
     const progressContainers = document.querySelectorAll(
       '.gift-progress-container, ' +
       '.cart-gift-progress, ' +
       '[class*="gift-progress"], ' +
       '[class*="gift-bar"]'
     );
     
     progressContainers.forEach(container => {
       if (container && container.classList.contains('gift-progress-hidden')) {
         container.style.display = '';
         container.style.opacity = '0';
         container.style.transform = 'translateY(-10px)';
         container.classList.remove('gift-progress-hidden');
         
         setTimeout(() => {
           container.style.opacity = '1';
           container.style.transform = 'translateY(0)';
           
           setTimeout(() => {
             container.classList.remove('gift-progress-transitioning');
           }, 300);
         }, 10);
       }
     });
   }

     conditionallyTriggerCartGiftManager(cartData) {
    // Try to initialize CartGiftManager if not available
    if (!window.cartGiftManager) {
      if (window.CartGiftManager) {
        try {
          window.cartGiftManager = new window.CartGiftManager();
        } catch (error) {
          return;
        }
      } else {
        return;
      }
    }
     
     const giftVariantId = window.cartGiftManager.giftVariantId;
     const threshold = window.cartGiftManager.threshold;
     
     // Check if automatic gift is already in cart
     const hasAutomaticGift = cartData.items.some(item => 
       item.variant_id == giftVariantId && 
       item.properties?._gift === 'true' &&
       !item.properties?._gift_modal &&
       !item.properties?._gift_modal_upsell
     );
     
     // Check if gift modal product conflicts with automatic gift
     const hasConflictingGiftModal = cartData.items.some(item => 
       item.variant_id == giftVariantId && 
       (item.properties?._gift_modal || item.properties?._gift_modal_upsell)
     );
     
     if (hasConflictingGiftModal) {
       return;
     }
     
     if (cartData.total_price >= threshold && !hasAutomaticGift) {
               // Single targeted trigger with longer delay to avoid conflicts
        if (typeof window.cartGiftManager.debounceUpdate === 'function') {
          window.cartGiftManager.debounceUpdate(800);
        }
       
               // Single cart event - NOT marked as from gift modal so CartGiftManager processes it
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:updated', { 
            detail: cartData, 
            bubbles: true 
          }));
        }, 100);
     }
   }

  // Enhanced validation that trusts Liquid data over JSON
  validateVariants() {
    const errors = [];
    
    // Validate main product variant
    const mainVariantId = this.getMainProductVariantId();
    
    if (this.mainProduct && this.mainProduct.variants) {
      const mainVariant = this.mainProduct.variants.find(v => v.id === parseInt(mainVariantId));
      if (!mainVariant) {
        errors.push(`Main product variant ${mainVariantId} not found`);
      } else if (!mainVariant.available) {
        errors.push(`Main product variant ${mainVariantId} is not available`);
      }
    } else {
      // Don't add error for main product if we have a variant ID (DOM fallback case)
      if (!mainVariantId) {
        errors.push('No main product variant available');
      }
    }
    
    // FIXED: Validate upsell variants using DOM data instead of incomplete JSON
    this.selectedUpsells.forEach(productId => {
      const variantId = this.selectedVariants.get(productId);
      
      if (!variantId) {
        errors.push(`No variant selected for upsell product ${productId}`);
        return;
      }
      
      // Check if the variant input exists in DOM (this means Liquid found it)
      const variantInput = this.modal.querySelector(`input[data-product-id="${productId}"][data-variant-id="${variantId}"]`);
      if (variantInput) {
        // Additional check: make sure it has price data
        if (!variantInput.dataset.variantPrice) {
          // Warning: Variant has no price data
        }
            } else {
        // Fallback: check if it's the button's default variant
        const button = this.modal.querySelector(`[data-product-id="${productId}"] .gift-upsell-toggle`);
        const buttonVariantId = button?.dataset?.variantId;
        
        if (buttonVariantId && parseInt(buttonVariantId) === parseInt(variantId)) {
          // Upsell variant validated via button data
        } else {
          errors.push(`Upsell variant ${variantId} not found for product ${productId}`);
        }
      }
    });
    
    return {
      valid: errors.length === 0,
      errors: errors
    };
  }

  prepareCartItems() {
    const items = [];
    
    // Add gift message if provided
    const messageInput = this.modal.querySelector('#gift-message');
    const giftMessage = messageInput ? messageInput.value.trim() : '';
    
    // Add main product
    const mainVariantId = this.getMainProductVariantId();
    if (mainVariantId) {
        const mainItem = {
        id: mainVariantId,
          quantity: 1,
        properties: {
          '_gift_modal': 'true'  // Always mark as gift modal item
        }
        };

            // Add gift message as line item property if provided  
        if (giftMessage) {
        mainItem.properties['_gift_message'] = giftMessage;
        }

        items.push(mainItem);
    }

    // FIXED: Add selected upsells - trust validated variant IDs
    this.selectedUpsells.forEach(productId => {
      const variantId = this.selectedVariants.get(productId);
      
      if (variantId) {
        // Since we already validated these variants, trust them
          const upsellItem = {
          id: variantId,
            quantity: 1,
          properties: {
            '_gift_modal_upsell': 'true'  // Always mark as gift modal upsell
          }
        };
        
                // Add gift message as line item property if provided
        if (giftMessage) {
          upsellItem.properties['_gift_message'] = giftMessage;
          }
          
          items.push(upsellItem);
        }
    });

    return items;
  }

  triggerCartUpdate() {
    // Trigger cart updated events
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    
    // Hide any gift messages that might appear in cart
    setTimeout(() => this.hideGiftMessagesInCart(), 100);
    
    // Try to open cart drawer
    if (window.wetheme?.cartDrawer?.openCartDrawer) {
      window.wetheme.cartDrawer.openCartDrawer();
    } else if (typeof $ !== 'undefined') {
      $('#cartSlideoutWrapper').trigger('cart:open');
    } else {
      // Fallback to cart page
        window.location.href = '/cart';
      }
    }

  hideGiftMessagesInCart() {
    // Find and hide any elements containing gift messages
    const elementsToCheck = document.querySelectorAll('.cart__meta-text, .cart-item-property, .cart-table-title div, [class*="cart"] div');
    
    elementsToCheck.forEach(element => {
      if (element.textContent && (element.textContent.includes('_gift_message:') || element.textContent.includes('Gift Message:'))) {
        element.style.display = 'none';
      }
    });
  }

  setupGiftMessageHiding() {
    // Hide gift messages on any cart update
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.hideGiftMessagesInCart(), 100);
    });

    // Also hide them periodically as a fallback
    setInterval(() => this.hideGiftMessagesInCart(), 2000);
  }

  setupEventListeners() {
    // Modal trigger button
    const modalTrigger = document.querySelector('.gift-feature-modal-trigger');
    if (modalTrigger) {
      modalTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    }

    // Modal close buttons  
    this.modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('gift-modal-overlay') || 
          e.target.classList.contains('gift-modal-close')) {
        this.closeModal();
      }
    });

    // Upsell toggle buttons
    this.modal.addEventListener('click', (e) => {
      const toggleButton = e.target.closest('.gift-upsell-toggle');
      if (toggleButton) {
        const productId = toggleButton.dataset.productId || 
                         toggleButton.closest('.gift-upsell-item')?.dataset?.productId;
        if (productId) {
          this.toggleUpsell(toggleButton);
        }
      }
    });

    // Variant selection (FIXED - properly updates selectedVariants)
    this.modal.addEventListener('change', (e) => {
      if (e.target.classList.contains('gift-variant-option') || 
          e.target.classList.contains('wetheme-custom-radio')) {
        const productId = e.target.dataset.productId;
        const optionIndex = parseInt(e.target.dataset.optionIndex);
        const value = e.target.value;
        
        if (productId && optionIndex !== undefined && value) {
          this.handleVariantChange(productId, optionIndex, value);
        }
      }
    });

    // CTA button
    const ctaButton = this.modal.querySelector('.gift-modal-cta-button, .gift-modal__add-to-cart');
    if (ctaButton) {
      ctaButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  handleVariantChange(productId, optionIndex, value) {
    const product = this.upsellProducts[productId];
    if (!product) {
      return;
    }

    // Find all current selected options for this product
    const currentOptions = [];
    const productSwatches = this.modal.querySelectorAll(`input[data-product-id="${productId}"].gift-variant-option, input[data-product-id="${productId}"].wetheme-custom-radio`);
    
    productSwatches.forEach(input => {
      if (input.checked) {
        const inputOptionIndex = parseInt(input.dataset.optionIndex);
        currentOptions[inputOptionIndex] = input.value;
      }
    });

    // FIXED: Enhanced variant matching that handles different data structures
    const matchingVariant = product.variants.find(variant => {
      // Handle variants that might not have options array properly set
      let variantOptions = variant.options;
      
      // If options array is missing or invalid, try to build it from option1, option2, option3
      if (!variantOptions || !Array.isArray(variantOptions) || variantOptions.length === 0) {
        variantOptions = [];
        if (variant.option1 !== null && variant.option1 !== undefined) variantOptions[0] = variant.option1;
        if (variant.option2 !== null && variant.option2 !== undefined) variantOptions[1] = variant.option2;
        if (variant.option3 !== null && variant.option3 !== undefined) variantOptions[2] = variant.option3;
      }

      // Check if this variant matches the selected options
      const isMatch = variantOptions.every((option, index) => {
        const selectedOption = currentOptions[index];
        const matches = !selectedOption || selectedOption === option;
        
        return matches;
      });

      return isMatch;
    });

    if (matchingVariant) {
      // UPDATE: This is the key fix - properly update the selectedVariants map
      this.selectedVariants.set(productId, matchingVariant.id);
      
      // Update price if this product is selected
      if (this.selectedUpsells.has(productId)) {
        this.updateTotalPrice();
      }
      
      // Update variant display (if you have a variant name display)
      this.updateVariantDisplay(productId, matchingVariant);
    }
  }

  updateVariantDisplay(productId, variant) {
    // Update any variant-specific displays like images or variant names
    const productContainer = this.modal.querySelector(`[data-product-id="${productId}"]`);
    if (productContainer) {
      // Update price display if needed
      const priceDisplay = productContainer.querySelector('.gift-upsell-price');
      if (priceDisplay && variant.price !== undefined) {
        priceDisplay.textContent = this.formatPrice(variant.price);
      }
      
      // Update image if variant has specific image
      const productImage = productContainer.querySelector('.gift-upsell-image img');
      if (productImage && variant.featured_image) {
        productImage.src = variant.featured_image.url;
        productImage.alt = variant.featured_image.alt || variant.title;
      }
    }
  }

  // Static initialization method
  static init() {
    if (!window.giftFeature) {
      window.giftFeature = new GiftFeature();
      window.giftFeature.setupGiftMessageHiding();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', GiftFeature.init);
} else {
  GiftFeature.init();
}

// Export for global use
window.GiftFeature = GiftFeature; 